import React, { useState, useEffect } from 'react';

import '../styles/SubscriptionComponent.css';
import { isValidEmail } from '../utils/Miscellaneous';
import countryCodesData from '../utils/Files/CountryCodes.json';
import SubscriptionErrorGrid from './SubscriptionErrorGrid';
import SubscriptionSuccessGrid from './SubscriptionSuccessGrid';
import usePostTransactionSubscription from '../hooks/usePostTransactionSubscription';

const SubscriptionComponent = ({ transaction, actionType }) => {
    const [alertType, setAlertType] = useState('both');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneWithCountryCode, setPhoneWithCountryCode] = useState('');
    const [selectedCountryCode, setSelectedCountryCode] = useState('');
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [countryName, setCountryName] = useState('');
    const [filteredCountries, setFilteredCountries] = useState([]);
    const { postTransactionSubscription, subscriptionResponse, postError } = usePostTransactionSubscription();  
    const [error, setError] = useState('');
    const [showResponse, setShowResponse] = useState(false);
    const [isErrorResponse, setIsErrorResponse] = useState(false);

    const handleAlertTypeChange = (event) => {
        setAlertType(event.target.value);
    };

    const handleEmailChange = (event) => {
        const { value } = event.target;
        setEmail(value);
        if (!isValidEmail(value)) {
            setEmailError('Invalid email address');
        } else {
            setEmailError('');
        }
    };

    const handlePhoneChange = (event) => {
        const { value } = event.target;
        if (!isNaN(value) && value.length <= 10) {
            setPhoneNumber(value);
            setPhoneWithCountryCode(selectedCountryCode + value);
        }

        if (value.length !== 10 || isNaN(value)) {
            setPhoneError('Phone number must be exactly 10 digits');
        } else {
            setPhoneError('');
        }
    };

    const handleCountryNameChange = (event) => {
        const { value } = event.target;
        setCountryName(value);
        const matchedCountries = countryCodesData.filter((country) =>
            country.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredCountries(matchedCountries);
        setSelectedCountryCode(
            matchedCountries.length > 0 ? matchedCountries[0].dial_code : ''
        );
    };

    const handleCountrySelect = (country) => {
        setCountryName(country.name);
        setSelectedCountryCode(country.dial_code);
        setFilteredCountries([]);
        setPhoneWithCountryCode((prev) =>
            country.dial_code + prev.replace(/^\+\d+/, '')
        );
    };

    const handleSubmit = async () => {
        if (emailError || phoneError) {
            alert('Please fix the errors before submitting.');
        } else {
            if (email || phoneWithCountryCode) {
                const subscriptionJsonRequest = {
                    senderName: transaction?.senderName || 'N/A',
                    receiverName: transaction?.receiverName || 'N/A',
                    documentType: transaction?.documentType || 'N/A',
                    documentReferenceType: transaction?.documentReferenceType || 'N/A',
                    subscriptionList: [
                        {
                            subscriptionType: 'EMAIL',
                            subscriptionValue: email,
                        },
                        {
                            subscriptionType: 'MOBILE',
                            subscriptionValue: phoneWithCountryCode,
                        },
                    ],
                    action: actionType.toUpperCase(),
                    statusType: alertType.toUpperCase(),
                };

                console.log("Subscription payload: ", subscriptionJsonRequest);
                try {
                    await postTransactionSubscription(subscriptionJsonRequest);
                    setShowResponse(true);
                } catch (error) {
                    console.error('Error posting subscription:', error);
                    setError('Failed to post the transaction subscription');
                }
            }
            else {
                console.error('To submit, an email or phone or both has to be included');
                setError('An email or phone or both has to be included');
            }
        }
    };

    useEffect(() => {
        if (subscriptionResponse) {
            console.log("subscription response: ", subscriptionResponse);
            setIsErrorResponse(subscriptionResponse.status === "ERROR");
        }
    }, [subscriptionResponse]);

    return (
        <div className="ReactModal__Content">
            <h1 className="transaction-status-header">
                {actionType === 'SUBSCRIBE' ? 'Subscription' : 'Unsubscription'}
            </h1>

            <div className="subscription-process">
                <div className="alert-type-section">
                    <div className="section-title">Alert Type</div>
                    <div className="radio-options">
                        <label>
                            <input
                                type="radio"
                                value="success"
                                checked={alertType === 'success'}
                                onChange={handleAlertTypeChange}
                            />
                            Success
                        </label>
                        <label>
                            <input
                                type="radio"
                                value="error"
                                checked={alertType === 'error'}
                                onChange={handleAlertTypeChange}
                            />
                            Error
                        </label>
                        <label>
                            <input
                                type="radio"
                                value="both"
                                checked={alertType === 'both'}
                                onChange={handleAlertTypeChange}
                            />
                            Both
                        </label>
                    </div>
                </div>

                <div className="input-section">
                    <label>Email Address:</label>
                    <input
                        type="email"
                        className="input-field"
                        value={email}
                        onChange={handleEmailChange}
                    />
                    {emailError && <div className="error-message">{emailError}</div>}
                </div>

                <div className="input-section">
                    <label>Country Name:</label>
                    <input
                        type="text"
                        className="input-field"
                        value={countryName}
                        onChange={handleCountryNameChange}
                        placeholder="Type country name..."
                    />
                    {filteredCountries.length > 0 && (
                        <ul className="country-dropdown">
                            {filteredCountries.map((country) => (
                                <li
                                    key={country.code}
                                    onClick={() => handleCountrySelect(country)}
                                    className="country-option"
                                >
                                    {country.name} ({country.dial_code})
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="input-section">
                    <label>Phone Number:</label>
                    <div className="phone-input">
                        <span className="country-code">{selectedCountryCode}</span>
                        <input
                            type="text"
                            className="input-field"
                            value={phoneNumber}
                            onChange={handlePhoneChange}
                            maxLength={10}
                        />
                    </div>
                    {phoneError && <div className="error-message">{phoneError}</div>}
                </div>

                <div className="button-section">
                    <button className="submit-button" onClick={handleSubmit}>
                        Submit
                    </button>
                </div>
            </div>

            {/* Conditional rendering for response */}
            {showResponse && (
                isErrorResponse ? (
                    <div className='subscription-response'>
                        <SubscriptionErrorGrid subscriptionResponse={subscriptionResponse} />
                    </div>
                ) : (
                    <div className='subscription-response'>
                        <SubscriptionSuccessGrid subscriptionResponse={subscriptionResponse} />
                    </div>
                )
            )}

        </div>
    );
};

export default SubscriptionComponent;
